import api from '@/lib/api/api';
import { validate as isUuid } from 'uuid';

const changedEvent = 'hatchet:trigger-again-changed';

function storageKey(tenantId: string, sourceRunId: string) {
  return `hatchet:trigger-again:${tenantId}:${sourceRunId}`;
}

export function triggerAgainState(tenantId: string, sourceRunId: string) {
  try {
    const requestId = window.sessionStorage.getItem(
      storageKey(tenantId, sourceRunId),
    );
    if (requestId === null) {
      return 'idle';
    }
    return isUuid(requestId) ? 'pending' : 'unavailable';
  } catch {
    return 'unavailable';
  }
}

export function subscribeTriggerAgain(listener: () => void) {
  window.addEventListener(changedEvent, listener);
  window.addEventListener('storage', listener);
  return () => {
    window.removeEventListener(changedEvent, listener);
    window.removeEventListener('storage', listener);
  };
}

function reserveRequest(key: string) {
  try {
    const storage = window.sessionStorage;
    const existing = storage.getItem(key);
    if (existing !== null) {
      if (!isUuid(existing)) {
        throw new Error('Saved request ID is invalid');
      }
      return existing;
    }
    const requestId = crypto.randomUUID();
    storage.setItem(key, requestId);
    if (storage.getItem(key) !== requestId) {
      throw new Error('Request ID was not saved');
    }
    window.dispatchEvent(new Event(changedEvent));
    return requestId;
  } catch {
    throw new Error(
      'This tab could not save the submission. No request was sent. Check browser storage and try again.',
    );
  }
}

export async function submitTriggerAgain(
  tenantId: string,
  sourceRunId: string,
) {
  const key = storageKey(tenantId, sourceRunId);
  const requestId = reserveRequest(key);
  const response = await api.v1WorkflowRunTriggerAgain(
    tenantId,
    sourceRunId,
    { requestId },
    { timeout: 30_000, xTenantId: tenantId },
  );
  const runId = response.data?.run?.metadata?.id;
  if (typeof runId !== 'string' || !isUuid(runId)) {
    throw new Error(
      'The server did not confirm the new run. Retry submission will check the same request.',
    );
  }
  try {
    // Another button may already have completed this request and started a new one.
    if (window.sessionStorage.getItem(key) === requestId) {
      window.sessionStorage.removeItem(key);
      window.dispatchEvent(new Event(changedEvent));
    }
  } catch {
    throw new Error(
      'The run was created, but this tab could not clear the submission. Retry submission will open the same run.',
    );
  }
  return runId;
}
